<template>
  <modal
    ref="modal"
    @before-open="onBeforeOpen"
    @before-close="onBeforeClose"
    @close="reset"
  >
    <span slot="activator">
      <slot name="activator"></slot>
    </span>
    <div v-if="create" slot="header">Create a new measurement</div>
    <div v-if="!create" slot="header">Edit measurement</div>
    <div slot="toolbar">
      <div class="ui grid left aligned">
        <div class="two wide column center aligned">
          <label>Product Type</label>
          <select
            v-if="types"
            v-model="product_type"
            class="ui fluid dropdown selection"
            name="product_type"
          >
            <option key="-1" value=""></option>
            <option v-for="t in types" :key="'p_type' + t" :value="t">
              {{ t }}
            </option>
          </select>
        </div>
        <div v-if="product_type" class="six wide column left bottom aligned">
          <button
            v-if="create"
            :class="{ loading: loading }"
            :disabled="loading"
            class="ui button blue"
            @click="createMeasure"
          >
            Save as {{ product_type }} measure
          </button>
          <button
            v-else
            :class="{ loading: loading }"
            :disabled="loading"
            class="ui button blue"
            @click="editMeasure"
          >
            Save as {{ product_type }} measure
          </button>
          <button @click="refreshMeasure" class="ui button orange">
            Reload remote
          </button>
        </div>
      </div>
    </div>

    <div>
      <div class="ui top attached header">Information</div>
      <div class="ui attached segment">
        <div class="ui grid">
          <div class="four wide column center aligned">
            <span v-if="errors.first('tall')" class="error">{{
              errors.first('tall')
            }}</span>
            <label v-else>Height(cm)</label>
            <div
              :class="{ error: errors.first('tall') }"
              class="ui fluid input"
            >
              <input
                v-model="tall"
                name="tall"
                v-validate="'numeric|required|min:3|max:3'"
                placeholder="Enter tall..."
              />
            </div>
          </div>

          <div class="four wide column center aligned">
            <span v-if="errors.first('weight')" class="error">{{
              errors.first('weight')
            }}</span>
            <label v-else>Weight(kg)</label>
            <div
              :class="{ error: errors.first('weight') }"
              class="ui fluid input"
            >
              <input
                v-model="weight"
                name="weight"
                v-validate="'numeric|required|min:2|max:3'"
                placeholder="Enter weight..."
              />
            </div>
          </div>

          <div class="four wide column center aligned">
            <span v-if="errors.first('year')" class="error">{{
              errors.first('year')
            }}</span>
            <label v-else>Year</label>
            <div
              :class="{ error: errors.first('year') }"
              class="ui fluid input"
            >
              <input
                v-model="year"
                name="year"
                v-validate="'numeric|required|min:4|max:4'"
                placeholder="Enter year of birth..."
              />
            </div>
          </div>
        </div>
      </div>
      <div class="ui attached segment">
        <div class="ui grid">
          <div class="three wide column center aligned">
            Pattern Base
            <button
              v-if="basesLoading"
              :class="{ loading: basesLoading }"
              :disabled="basesLoading"
              class="ui button white w100"
            ></button>
            <select
              v-else
              v-model="pattern_base"
              class="ui fluid dropdown selection"
              name="pattern_base"
            >
              <option key="-1" value=""></option>
              <option
                v-for="(option, index) in baseNames"
                :key="'base' + index"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
          </div>
          <div class="two wide column center aligned">
            Base Size
            <button
              v-if="sizesLoading"
              :class="{ loading: loading }"
              :disabled="sizesLoading"
              class="ui button white w100"
            ></button>
            <select
              v-else
              v-model="base_size"
              class="ui fluid dropdown selection"
              name="base_size"
            >
              <option key="-1" value=""></option>
              <option
                v-for="option in base_size_options[product_type]"
                :key="option.value"
                :value="option"
              >
                {{
                  product_type === 'shirt' ? baseLabelsShirt[option] : option
                }}
              </option>
            </select>
          </div>
          <div class="three wide column center aligned">
            Product Style
            <button
              v-if="stylesLoading"
              :class="{ loading: stylesLoading }"
              :disabled="stylesLoading"
              class="ui button white w100"
            ></button>
            <select
              v-else
              v-model="product_style"
              class="ui fluid dropdown selection"
              name="product_style"
            >
              <option key="-1" value=""></option>
              <option
                v-for="option in styles"
                :key="option.value"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
          </div>

          <div
            v-if="product_type === 'shirt' && watchExtras.length > 0"
            class="three wide column center aligned"
          >
            Watch Extra
            <select
              v-model="watch_extra"
              class="ui fluid dropdown selection"
              name="watch_extra"
            >
              <option key="-1" value=""></option>
              <option
                v-for="option in watchExtras"
                :key="'we' + option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
          </div>

          <div
            v-if="product_type === 'shirt' && watchExtras.length > 0"
            class="three wide column center aligned"
          >
            Tucked Extra
            <button
              v-if="tuckedExtrasLoading"
              :class="{ loading: tuckedExtrasLoading }"
              :disabled="tuckedExtrasLoading"
              class="ui button white w100"
            ></button>
            <select
              v-else
              v-model="tucked_extra"
              class="ui fluid dropdown selection"
              name="tucked_extra"
            >
              <option key="-1" value=""></option>
              <option
                v-for="option in tuckedExtras"
                :key="'te' + option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="ui attached segment">
        <div class="ui grid">
          <div class="eight wide column center aligned">
            <span v-if="errors.first('name')" class="error">{{
              errors.first('name')
            }}</span>
            <label v-else>Name</label>
            <div
              class="ui fluid input"
              :class="{ error: errors.first('name') }"
            >
              <input
                v-model="calc_name"
                name="name"
                v-validate="'required'"
                placeholder="Enter name"
              />
            </div>
          </div>
          <div class="eight wide column center aligned">
            <div class="ui grid">
              <div class="eight wide column center aligned">
                <label>Comment</label>
                <div class="ui fluid input">
                  <input
                    v-model="comment"
                    name="comment"
                    placeholder="Comment"
                  />
                </div>
              </div>
              <div class="eight wide column center aligned">
                <label>Public Comment</label>
                <div class="ui fluid input">
                  <input
                    v-model="comment_public"
                    name="comment_public"
                    placeholder="Public Comment"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="product_type !== 'shirt' && editors.length > 0"
        class="ui segment measures_list"
      >
        <datatable
          :columns="measureFields"
          :fetch="fetchData"
          :empty_cell="true"
          table_class="ui table single line left aligned fixed collapsing striped"
        >
          <thead slot="columns_content">
            <tr>
              <td>Name</td>
              <td class="measure_garment">Measure</td>
            </tr>
            <tr v-for="(column, index) in editors" :key="index">
              <td>{{ column.name }}</td>
              <td v-if="measure" class="measure_body">
                <select
                  v-if="measureInputs[column.id]"
                  v-model="measure[column.id]"
                  :name="'body_' + index"
                  class="ui dropdown selection"
                  style="height: 40px"
                >
                  <option
                    v-for="option in measureInputs[column.id]"
                    :key="'b' + option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
              </td>
            </tr>
          </thead>
        </datatable>
      </div>
      <!-- shirt editor, including visualized measurements and slider/dropdown alternations -->
      <div v-if="product_type === 'shirt'" :key="shirtEditKey">
        <shirt-edit
          :smart-measures="smart"
          :init-measures="shirt_to_edit"
          :set-event="setGar"
          :encolure="measure.encolure"
          :pente_epaule_g="measure.pente_epaule_g"
          :pente_epaule_d="measure.pente_epaule_d"
          :pente_dos="measure.pente_dos"
          :stooped="measure.stooped"
          :position_emmanchure="measure.position_emmanchure"
          :front_yoke="measure.front_yoke"
          :petite_poitrine="measure.petite_poitrine"
          :petite_carrure="measure.petite_carrure"
          :repart_taille="measure.repart_taille"
          :repart_bassin="measure.repart_bassin"
          :longueur_avant="measure.longueur_avant"
          :sleeve_pleats="measure.sleeve_pleats"
          :smart-measurements="smartMeasurements"
          class="shirt_edit"
          @set="setGar"
        ></shirt-edit>
      </div>
    </div>
  </modal>
</template>

<style scoped>
.error {
  color: #c00;
}

.w100 {
  width: 100%;
  height: 66%;
}

select {
  padding-top: 8px !important;
}

input {
  text-align: center !important;
}

.measure_body {
  background-color: #d5aaff;
}

td {
  vertical-align: middle;
}

.measure_garment {
  background-color: #afcbff;
}

.shirt_edit {
  width: 90%;
  margin: 10px;
}
</style>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import _ from 'lodash'
import ShirtEdit from './ShirtEdit'

const resetMixin = {
  methods: {
    resetData() {
      this.resetValidator()
      const defaultData = this.$options.data()
      Object.assign(this.$data, defaultData)
    }
  }
}

export default {
  name: 'Measure',
  components: {
    'shirt-edit': ShirtEdit
  },
  props: {
    fieldNames: {
      type: Object,
      default: null
    },
    measure_to_edit: {
      type: Object,
      default: null
    }
  },
  mixins: [resetMixin],
  data() {
    return {
      currentMeasure: {},
      saved: false,
      key: 0,
      loading: false,
      create: true,
      calc_name: 'Mesures sur chemise',
      weight: '',
      tall: '',
      year: '',
      age: '',
      measure: {
        neck: 0,
        chest: 0,
        waist: 0,
        hips: 0,
        shoulders: 0,
        sleever: 0,
        sleevel: 0,
        back: 0,
        wristr: 0,
        wristl: 0,
        armhole: 0,
        biceps: 0,
        elbow: 0,

        watch_extra: 0,
        tucked_extra: '',

        product_type: '',
        base_size: '',
        product_style: '',
        pattern_base: '',
        comment: '',
        comment_public: '',

        pants_waist: 0,
        pants_tight: 0,
        calf: 0,
        bottom: 0,
        r_leg_length: 0,
        l_leg_length: 0,
        front_waist: 0,
        back_waist: 0,
        morphology: '',
        tight: '',
        buttocks: '',

        encolure: 0, // shirt_alter_1
        pente_epaule_g: 0, // shirt_alter_2
        pente_epaule_d: 0, // shirt_alter_3
        pente_dos: 0, // shirt_alter_4
        stooped: 0, // shirt_alter_5
        position_emmanchure: 0, // shirt_alter_6
        front_yoke: 0, // shirt_alter_7
        petite_poitrine: 0, // shirt_alter_8
        petite_carrure: 0, // shirt_alter_9
        repart_taille: 2, // shirt_alter_10 (default +2cm see => #581)
        repart_bassin: 0, // shirt_alter_11
        longueur_avant: 0, // shirt_alter_12
        sleeve_pleats: 'S2P' // shirt_alter_13 (default "2 plis" see => #581)
      },

      measure_type: 'tops',
      measureFields: [],
      measureInputs: {},
      product_type: '',
      base_size: '',
      base_size_options: {
        shirt: _.range(88, 151, 1),
        chinos: _.range(36, 60, 2),
        jeans: _.range(28, 40, 1),
        trousers: _.range(42, 53, 1).concat(_.range(54, 64, 2)),
        jacket: _.range(28, 151, 1)
      },
      sizesLoading: false,
      product_style: '',
      pattern_base: '',
      watch_extra: 0,
      tucked_extra: '',
      morphology: '',
      tight: '',
      buttocks: '',
      comment: '',
      comment_public: '',

      types: [],

      bases: [],
      basesLoading: false,

      styles: [],
      stylesLoading: false,

      editors: [],

      watchExtras: [],
      watchExtrasLoading: false,

      tuckedExtras: [],
      tuckedExtrasLoading: false,

      smartRaw: {},
      smartMap: {},
      baseLabelsShirt: {},

      smart: {
        chest: 0,
        waist: 0,
        hips: 0,
        cuff: 0,
        sleeve_length: 0,
        back_shoulder: 0,
        biceps: 0,
        back_length: 0,
        collar_real: 0
      },

      smartMeasurements: {
        chest: 0,
        waist: 0,
        hips: 0,
        cuff: 0,
        sleeve_length: 0,
        back_shoulder: 0,
        biceps: 0,
        back_length: 0,
        collar_real: 0
      },

      shirt_to_edit: {
        chest: 0,
        waist: 0,
        hips: 0,
        wristl: 0,
        wristr: 0,
        sleevel: 0,
        sleever: 0,
        shoulders: 0,
        biceps: 0,
        back: 0,
        neck: 0
      },
      shirtEditKey: 0
    }
  },
  computed: {
    ...mapGetters({
      countries: 'getCountries',
      currentCustomer: 'getCurrentCustomer'
    }),
    calc_name_auto() {
      const auto = ['Mesures sur chemise', 'Mesures sur corps']
      this.types.forEach((item) => {
        if (item) {
          let productType = item
          if (item === 'shirt') productType = 'chemise'
          auto.push(`Mesures sur ${productType}`)
        }
      })
      return auto
    },

    baseNames() {
      return this.refreshBase()
    }
  },

  watch: {
    saved(val) {
      if (val) return
      this.product_type = this.product_type ? this.product_type : 'shirt'
    },
    measure_to_edit() {
      this.refreshMeasure()
    },

    product_type(newVal) {
      if (newVal) {
        if (this.calc_name_auto.includes(this.calc_name)) {
          // meaning it's a default/auto name instead of manual name
          let productType = newVal
          if (newVal === 'shirt') productType = 'chemise'
          this.calc_name = `Mesures sur ${productType}`
        }
        if (newVal === 'shirt' || newVal === 'jacket') {
          this.measure_type = 'tops'
        } else {
          this.measure_type = 'bottoms'
        }
        this.refreshBase()
        this.getStyles(newVal)
      } else {
        this.measure_type = 'tops'
        this.calc_name = 'Mesures sur chemise'
      }
    },

    pattern_base(newVal, oldVal) {
      if ((oldVal === 'MSH01' || oldVal === 'MSH02') && newVal === '') {
        this.$set(this.base_size_options, 'shirt', [])
      } else {
        this.getBaseMeasurements()
        this.getSizes(this.product_type, newVal)
      }
    },

    bases() {
      this.getBaseMeasurements()
    },

    base_size() {
      this.refreshSmart()
    },

    smartMap() {
      if (!this.base_size) return
      this.smart = this.smartMap[this.base_size]
    },
    tall() {
      if (this.tall && this.weight && this.year) this.getSmartMeasurement()
    },
    weight() {
      if (this.tall && this.weight && this.year) this.getSmartMeasurement()
    },
    year() {
      if (this.tall && this.weight && this.year) this.getSmartMeasurement()
    }
  },
  mounted() {},
  methods: {
    onBeforeOpen() {
      this.refreshMeasure()
      this.resetValidator()
      this.getTypes()
      this.getBases()
      this.product_type = this.measure.product_type
        ? this.measure.product_type
        : 'shirt'
    },

    fetchData(callback) {
      callback([{}])
    },

    createMeasure() {
      this.$validator.validate().then((result) => {
        if (!result) {
          // eslint-disable-next-line
          alert('Your input have errors, current edit is not saved!')
          return
        }
        this.measure.product_type = this.product_type
        this.measure.comment = this.comment
        this.measure.comment_public = this.comment_public
        this.measure.base_size = this.base_size
        if (this.product_type === 'shirt') {
          this.measure.watch_extra = this.watch_extra
          this.measure.tucked_extra = this.tucked_extra
          if (this.base_size) {
            this.measure.collar_size = this.smartMap[this.base_size].collar_size
          }
        }
        this.measure.product_style = this.product_style
        this.measure.pattern_base = this.pattern_base
        this.measure.age = this.year
        this.measure.poids = this.weight
        this.measure.taille = this.tall
        this.measure.name = this.calc_name
        this.measure.date = moment().format('MMM Do YY')
        this.measure.id_customer = this.currentCustomer.id

        this.loading = true
        axios
          .post('/measures', this.measure)
          .then((response) => {
            this.loading = false
            this.saved = true
            // Emit the event and close the modal when axios finished.
            this.$emit('create', response.data.measure)

            this.$refs.modal.close()
            this.$store.dispatch('createMeasure', response.data.measure)
          })
          .catch((e) => {
            //
            if (e.response.data && e.response.data.errors) {
              // Convert the format key->value received from the server to the validation of VueJS
              this.setValidatorErrors(e.response.data.errors)
            }
            // eslint-disable-next-line
            console.log(e)
            this.loading = false
          })
      })
    },

    editMeasure() {
      this.$validator.validate().then((result) => {
        if (!result) {
          // eslint-disable-next-line
          alert('Your input have errors, current edit is not saved!')
          return
        }
        this.measure.product_type = this.product_type
        this.measure.comment = this.comment
        this.measure.comment_public = this.comment_public
        this.measure.base_size = this.base_size
        if (this.product_type === 'shirt') {
          this.measure.pants_waist = 0
          this.measure.pants_tight = 0
          this.measure.calf = 0
          this.measure.bottom = 0
          this.measure.r_leg_length = 0
          this.measure.l_leg_length = 0
          this.measure.front_waist = 0
          this.measure.back_waist = 0
          this.measure.morphology = ''
          this.measure.tight = ''
          this.measure.buttocks = ''

          this.measure.watch_extra = this.watch_extra
          this.measure.tucked_extra = this.tucked_extra
          if (this.base_size) {
            this.measure.collar_size = this.smartMap[this.base_size].collar_size
          }
        } else {
          this.measure.neck = 0
          this.measure.chest = 0
          this.measure.waist = 0
          this.measure.hips = 0
          this.measure.shoulders = 0
          this.measure.sleever = 0
          this.measure.sleevel = 0
          this.measure.back = 0
          this.measure.wristr = 0
          this.measure.wristl = 0
          this.measure.armhole = 0
          this.measure.biceps = 0
          this.measure.elbow = 0
          this.measure.watch_extra = 0
          this.measure.tucked_extra = ''
          this.measure.encolure = 0 // shirt_alter_1
          this.measure.pente_epaule_g = 0 // shirt_alter_2
          this.measure.pente_epaule_d = 0 // shirt_alter_3
          this.measure.pente_dos = 0 // shirt_alter_4
          this.measure.stooped = 0 // shirt_alter_5
          this.measure.position_emmanchure = 0 // shirt_alter_6
          this.measure.front_yoke = 0 // shirt_alter_7
          this.measure.petite_poitrine = 0 // shirt_alter_8
          this.measure.petite_carrure = 0 // shirt_alter_9
          this.measure.repart_taille = 0 // shirt_alter_10
          this.measure.repart_bassin = 0 // shirt_alter_11
          this.measure.longueur_avant = 0 // shirt_alter_12
          this.measure.sleeve_pleats = '' // shirt_alter_13
        }
        this.measure.product_style = this.product_style
        this.measure.pattern_base = this.pattern_base
        this.measure.age = this.year
        this.measure.poids = this.weight
        this.measure.taille = this.tall
        this.measure.name = this.calc_name
        this.measure.modified = true
        this.measure.id_customer = this.currentCustomer.id

        this.loading = true
        axios
          .put(`/measures/${this.measure.id}`, this.measure)
          .then((response) => {
            this.loading = false
            this.saved = true
            // Emit the event and close the modal when axios finished.
            this.$store.dispatch('editMeasure', response.data.measure)

            this.$refs.modal.close()
          })
          .catch((e) => {
            //
            if (e.response.data && e.response.data.errors) {
              // Convert the format key->value received from the server to the validation of VueJS
              this.setValidatorErrors(e.response.data.errors)
            }
            // eslint-disable-next-line
            console.log(e)
            this.loading = false
          })
      })
    },

    reset() {
      if (!this.saved) {
        return
      }
      this.resetData()
      this.$store.dispatch('setCurrentMeasure', null)
      this.$emit('reset')
    },

    onBeforeClose() {
      if (this.saved) return
      if (this.create) {
        this.createMeasure()
      } else {
        this.editMeasure()
      }
    },

    onMod(mod) {
      this.m_modifiers[mod.key] = mod.val
    },

    isNumeric(value) {
      return typeof value === 'number' && !Number.isNaN(value)
    },

    getTypes() {
      axios.get('/types').then((response) => {
        this.types = response.data.types
      })
    },
    getBases() {
      this.basesLoading = true
      axios.get('/bases').then((response) => {
        this.bases = response.data.bases
        this.pattern_base = 'MSH02' // (default "MSH02" see => #581. Defined here to trigger the watcher for "base size" selector)
        this.basesLoading = false
      })
    },
    getStyles(type) {
      this.stylesLoading = true
      axios.get('/styles', { params: { type } }).then((response) => {
        this.styles = response.data.styles
        this.stylesLoading = false
      })
    },
    getSizes(type, baseName) {
      if (!baseName) return
      this.sizesLoading = true
      axios.get('/sizes', { params: { baseName } }).then((response) => {
        this.smartRaw = response.data.sizes
        const keys = []
        const labels = []
        let key = ''
        let label = ''
        this.smartRaw.forEach((item) => {
          key = item.base_size
          label = `${item.base_size}/${item.collar_size}`
          keys.push(key)
          labels.push(label)

          const bodyMeasurePair = {}
          Object.keys(item).forEach((bodyPart) => {
            this.$set(bodyMeasurePair, bodyPart, parseFloat(item[bodyPart]))
          })
          this.$set(this.smartMap, key, bodyMeasurePair)
          this.$set(this.baseLabelsShirt, key, label)
        })
        this.$set(this.base_size_options, type, keys)
        this.sizesLoading = false
        this.refreshSmart()
      })
    },
    refreshSmart() {
      if (!this.base_size) {
        Object.keys(this.smart).forEach((key) => {
          this.$set(this.smart, key, 0)
        })
      } else if (this.smartMap) {
        this.smart = this.smartMap[this.base_size]
      }
      this.shirtEditKey += 1
    },
    refreshBase() {
      return this.bases
        .filter((el) => {
          if (this.product_type === 'shirt') {
            return el.name === 'MSH01' || el.name === 'MSH02'
          }
          return el.name === this.product_type
        })
        .map((el) => el.name)
    },
    getBaseMeasurements() {
      const base = this.bases.find((el) => el.name === this.pattern_base)
      if (!(base && base.data.measurements)) {
        return
      }
      // get the selected base's definition about measurements fields
      // if product type is shirt, the measurements are edited in a visulized way, for other product types, use table and drop down or number input
      // shirt type products generally has watch extra field, set field select options by base definition
      if (this.product_type === 'shirt') {
        this.getWatchExtras() // take v or not?
        this.getTuckedExtras() // take v or not?
      } else {
        this.getMeasureFields()
      }
    },
    getMeasureFields() {
      // if the product type is not shirt, you will edit the measurements in a non-visual way
      // collect the base defined measurements fields and asign to page to dispaly and edit
      const base = this.bases.find((el) => el.name === this.pattern_base)
      if (base && base.data.measurements) {
        this.editors = []
        base.data.measurements.forEach((m) => {
          this.editors.push({
            id: m.key,
            name: this.fieldNames[m.key]
          })

          let options = []
          if (m.type === 'NUMBER') {
            // should check more about min max and step
            options = _.range(m.minValue, m.maxValue + m.step, m.step)
          } else if (m.key === 'morphology') {
            options = ['', 'standard', 'athletic', 'below_belly', 'over_belly']
          } else if (m.key === 'tight') {
            options = ['', 'standard', 'thick', 'thin']
          } else if (m.key === 'buttocks') {
            options = ['', 'standard', 'flat', 'rounded']
          }
          this.$set(this.measureInputs, m.key, options)
        })
      }
    },
    getWatchExtras() {
      this.watchExtrasLoading = true
      // get current selected base
      const base = this.bases.find((el) => el.name === this.pattern_base)
      if (base && base.data.measurements) {
        // get base watch extra allowed values definition
        const we = base.data.measurements.find((el) => el.key === 'watch_extra')
        if (we) {
          // set watch extra field select options
          const options = _.range(we.minValue, we.maxValue + we.step, we.step)
          this.watchExtras = options
        } else {
          this.watchExtras = []
        }
      }
      this.watchExtrasLoading = false
    },
    getTuckedExtras() {
      this.tuckedExtrasLoading = true
      const base = this.bases.find((el) => el.name === this.pattern_base)
      if (base && base.data.measurements) {
        const te = base.data.measurements.find(
          (el) => el.key === 'tucked_extra'
        )
        if (te) {
          const options = ['tucked', 'untucked', 'versatile']
          this.tuckedExtras = options
        } else {
          this.tuckedExtras = []
        }
      }
      this.tuckedExtrasLoading = false
    },
    getSmartMeasurement() {
      this.smartMeasurements.collar_real =
        Math.round(this.getSmartCollarReal() * 10) / 10
      this.smartMeasurements.chest = Math.round(this.getSmartChest() * 10) / 10
      this.smartMeasurements.waist = Math.round(this.getSmartWaist() * 10) / 10
      this.smartMeasurements.hips = Math.round(this.getSmartHips() * 10) / 10
      this.smartMeasurements.cuff = Math.round(this.getSmartCuffs() * 10) / 10// poignets
      this.smartMeasurements.sleeve_length =
        Math.round(this.getSmartSleeveLength() * 10) / 10
      this.smartMeasurements.back_shoulder =
        Math.round(this.getSmartBackShoulder() * 10) / 10// carrure
      this.smartMeasurements.back_length =
        Math.round(this.getSmartBackLength() * 10) / 10
    },
    setGar(val) {
      if ('name' in val && 'measure' in val) {
        this.$set(this.measure, val.name, val.measure)
        switch (val.name) {
          case 'demi_poitrine':
            this.$set(this.shirt_to_edit, 'chest', val.measure)
            break
          case 'demi_taille':
            this.$set(this.shirt_to_edit, 'waist', val.measure)
            break
          case 'demi_bassin':
            this.$set(this.shirt_to_edit, 'hips', val.measure)
            break
          default:
            this.$set(this.shirt_to_edit, val.name, val.measure)
        }
      }
    },
    setValidatorErrors(errors) {
      this.$validator.errors.clear()
      Object.keys(errors).forEach((key) => {
        this.$validator.errors.add({
          field: key,
          msg: errors[key]
        })
      })
    },
    resetValidator() {
      this.$validator.pause()
      this.$nextTick(() => {
        this.$validator.errors.clear()
        this.$validator.fields.items.forEach((field) => field.reset())
        this.$validator.fields.items.forEach((field) =>
          this.errors.remove(field)
        )
        this.$validator.resume()
      })
    },
    refreshMeasure() {
      if (!this.measure_to_edit || !this.measure_to_edit.id) return
      this.create = false

      axios.get(`/measure/${this.measure_to_edit.id}`).then((response) => {
        this.currentMeasure = response.data.measure
        this.$store.dispatch('setCurrentMeasure', response.data.measure)

        // Copy all the fields from the currentMeasure to the editable one.
        Object.keys(this.measure).forEach((i) => {
          if (this.currentMeasure[i] === undefined) return
          this.measure[i] = this.isNumeric(this.measure[i])
            ? parseFloat(this.currentMeasure[i])
            : this.currentMeasure[i]
        })
        this.measure.id = this.currentMeasure.id

        this.shirt_to_edit.chest = this.currentMeasure.demi_poitrine
        this.shirt_to_edit.waist = this.currentMeasure.demi_taille
        this.shirt_to_edit.hips = this.currentMeasure.demi_bassin
        this.shirt_to_edit.wristl = this.currentMeasure.poignet_g
        this.shirt_to_edit.wristr = this.currentMeasure.poignet_d
        this.shirt_to_edit.sleevel = this.currentMeasure.manche_g
        this.shirt_to_edit.sleever = this.currentMeasure.manche_d
        this.shirt_to_edit.shoulders = this.currentMeasure.carrure
        this.shirt_to_edit.biceps = this.currentMeasure.biceps
        this.shirt_to_edit.back = this.currentMeasure.longueur
        this.shirt_to_edit.neck = this.currentMeasure.col
        this.shirtEditKey += 1

        this.calc_name = this.currentMeasure.name
        this.weight = this.currentMeasure.poids
        this.tall = this.currentMeasure.taille
        this.year = this.currentMeasure.age // the age stored in db is actually year, as posted from form field year
        this.age =
          new Date().getFullYear() - parseInt(this.currentMeasure.age, 10) // the age used on page should be age within natual lifespan

        this.product_type = this.currentMeasure.product_type
          ? this.currentMeasure.product_type
          : 'shirt'
        this.base_size = this.currentMeasure.base_size
        this.product_style = this.currentMeasure.product_style
        this.pattern_base = this.currentMeasure.pattern_base
        this.morphology = this.currentMeasure.morphology
        this.tight = this.currentMeasure.tight
        this.buttocks = this.currentMeasure.buttocks
        this.watch_extra = this.currentMeasure.watch_extra
        this.tucked_extra = this.currentMeasure.tucked_extra
        this.comment = this.currentMeasure.comment
        this.comment_public = this.currentMeasure.comment_public
      })
    },
    getSmartCollarReal() {
      return 40 + -0.06 * (this.tall - 180) + 0.1666 * (this.weight - 80) + 1
    },
    getSmartChest() {
      return (
        ((102.2 + -0.25 * (this.tall - 180) + 0.6333 * (this.weight - 80)) *
          1.08) /
        2
      )
    },
    getSmartWaist() {
      return (
        ((93.7 + -0.46 * (this.tall - 180) + 0.85 * (this.weight - 80)) *
          1.07) /
        2
      )
    },
    getSmartHips() {
      return (
        ((102 + -0.18 * (this.tall - 180) + 0.5833 * (this.weight - 80)) *
          1.06) /
        2
      )
    },
    getSmartCuffs() {
      return this.getSmartBackShoulder() / 2.05
    },
    getSmartSleeveLength() {
      return (this.tall * 3) / 8 - (1 / 70) * this.tall
    },
    getSmartBackShoulder() {
      return (
        this.tall / 3.01 +
        (this.weight / ((this.tall - 100 + this.year / 10) * 0.9) - 1) * 20
      )
    },
    getSmartBackLength() {
      return (this.tall * 7) / 15.7
    }
  }
}
</script>



// WEBPACK FOOTER //
// MeasureModal.vue?a763864c